/**
 * register.tsx — Register-Route
 *
 * Registrierungsformular via Devise/JWT Registration-Endpoint.
 * POST /api/v2/auth/register für neue Benutzer.
 */
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { queryClient } from '../lib/query-client'

// i18n-Fix (AUTH-Block): Zod-Fehlertexte kamen vormals hartkodiert auf Deutsch,
// obwohl auth.json:validation.* bereits DE+EN-Keys dafuer bereithielt (tote Keys).
// Schema wird innerhalb der Komponente mit t() gebaut, damit Sprachwechsel greift.
// Mindestlaenge 12 (nicht 8!) — Backend erzwingt via devise_security.rb
// config.password_length = 12..128 (verifiziert live: 8-stellige Passwoerter
// werden vom Server mit 422 "too short (minimum is 12 characters)" abgelehnt).
//
// Name-Feld: rein clientseitig (siehe unten, wird NICHT an die API gesendet) —
// Api::V2::AuthController#registration_params permittet nur email/password/
// password_confirmation, users-Tabelle hat keine name-Spalte (siehe schema.rb).
// Kein Confirm-Password-Feld mehr (Vorlage register.html hat keins) —
// password_confirmation wird beim Submit clientseitig auf password gespiegelt,
// der Backend-Contract (required password_confirmation) bleibt dadurch erfuellt.
function buildRegisterSchema(t: (key: string) => string) {
  return z.object({
    name: z.string().optional(),
    email: z.string().email(t('validation.invalidEmail')),
    password: z.string().min(12, t('validation.passwordMinLength')),
    agreeToTerms: z.literal(true, {
      errorMap: () => ({ message: t('validation.tosRequired') }),
    }),
  })
}

type RegisterInput = z.infer<ReturnType<typeof buildRegisterSchema>>

// ponytail: simple heuristic, not a real zxcvbn dictionary/pattern check —
// upgrade to the zxcvbn package if product wants entropy-accurate scoring.
function computePasswordStrength(password: string): { score: 0 | 1 | 2 | 3; label: string } {
  if (!password) return { score: 0, label: 'empty' }
  let variety = 0
  if (/[a-z]/.test(password)) variety++
  if (/[A-Z]/.test(password)) variety++
  if (/[0-9]/.test(password)) variety++
  if (/[^a-zA-Z0-9]/.test(password)) variety++

  let score: 0 | 1 | 2 | 3 = 0
  if (password.length >= 12 && variety >= 3) score = 3
  else if (password.length >= 12 && variety >= 2) score = 2
  else if (password.length >= 8) score = 1

  const label = ['weak', 'weak', 'fair', 'strong'][score]
  return { score, label }
}

const REGISTER_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v2/auth/register`
const LOGIN_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v2/auth/login`

export const Route = createFileRoute('/register')({
  beforeLoad: () => {
    if (useAuthStore.getState().isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: RegisterPage,
})

function RegisterPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const setTokens = useAuthStore((s) => s.setTokens)
  const [formError, setFormError] = useState<string | null>(null)

  const registerSchema = useMemo(() => buildRegisterSchema(t), [t])

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  })

  const passwordValue = watch('password') ?? ''
  const strength = useMemo(() => computePasswordStrength(passwordValue), [passwordValue])

  async function onSubmit(data: RegisterInput) {
    setFormError(null)
    try {
      const res = await fetch(REGISTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          // Kein Confirm-Feld in der UI (siehe buildRegisterSchema) — Backend
          // verlangt password_confirmation dennoch als Pflichtfeld.
          password_confirmation: data.password,
        }),
      })

      if (res.ok) {
        // /api/v2/auth/register liefert nur einen Legacy-`user.access_token`
        // (kein Doorkeeper-Paar mit refresh_token/expires_in, siehe
        // auth_controller.rb#register) — für den Welcome-Flow (F1/F2-Modale
        // rufen authentifizierte v2-Endpunkte auf) brauchen wir das echte
        // Token-Paar. Statt einen neuen Backend-Endpunkt zu verlangen,
        // nutzen wir den bereits vorhandenen /auth/login mit denselben
        // frisch eingegebenen Zugangsdaten (kein Passwort-Speichern noetig,
        // liegt noch im Formularstate).
        try {
          const loginRes = await fetch(LOGIN_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.email, password: data.password }),
          })
          if (loginRes.ok) {
            const loginJson = (await loginRes.json()) as {
              token?: string
              refresh_token?: string
              expires_in?: number
            }
            if (loginJson.token) {
              setTokens(loginJson.token, loginJson.refresh_token, loginJson.expires_in)
              queryClient.clear()
            }
          }
        } catch {
          // Auto-Login-Fehler blockt die Registrierung nicht: /welcome greift
          // dann selbst den Auth-Guard und leitet auf /login um.
        }
        void navigate({ to: '/welcome' })
        return
      }

      if (res.status === 422) {
        const json = (await res.json()) as { errors?: Record<string, string[]> }
        if (json.errors) {
          for (const [field, messages] of Object.entries(json.errors)) {
            const msg = Array.isArray(messages) ? messages[0] : String(messages)
            if (field === 'email') setError('email', { message: msg })
            else if (field === 'password' || field === 'password_confirmation')
              setError('password', { message: msg })
            else setFormError(msg)
          }
        } else {
          setFormError(t('register.errors.registrationFailed'))
        }
        return
      }

      setFormError(t('register.errors.unexpectedError'))
    } catch {
      setFormError(t('register.errors.networkError'))
    }
  }

  return (
    <div className="auth-shell" data-testid="register-page">
      {/* Linke Spalte: Formular */}
      <div className="auth-form-wrap">
        <div className="auth-form">
          {/* Logo-Mark */}
          <div className="row" style={{ gap: 10 }}>
            <div className="logo-mark">H</div>
            <div style={{ fontWeight: 600 }}>HeliumTracker</div>
          </div>

          <div>
            <h1 style={{ fontSize: 28 }}>{t('register.title')}</h1>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {t('register.subtitle')}
            </p>
          </div>

          {formError && (
            <div
              role="alert"
              aria-live="polite"
              className="alert alert-error"
              data-testid="form-error"
            >
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label={t('register.formAriaLabel')} style={{ display: 'contents' }}>
            <div>
              <label className="label" htmlFor="reg-name">{t('register.nameLabel')}</label>
              <input
                id="reg-name"
                type="text"
                className="input"
                placeholder={t('register.namePlaceholder')}
                aria-label={t('register.nameAriaLabel')}
                {...register('name')}
              />
            </div>

            <div>
              <label className="label" htmlFor="reg-email">{t('register.emailLabel')}</label>
              <input
                id="reg-email"
                type="email"
                className="input"
                aria-label={t('register.emailAriaLabel')}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'reg-email-error' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <span
                  id="reg-email-error"
                  role="alert"
                  aria-live="polite"
                  className="field-error"
                >
                  {errors.email.message}
                </span>
              )}
            </div>

            <div>
              <label className="label" htmlFor="reg-password">{t('register.passwordLabel')}</label>
              <input
                id="reg-password"
                type="password"
                className="input"
                placeholder={t('register.passwordPlaceholder')}
                aria-label={t('register.passwordAriaLabel')}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'reg-password-error' : undefined}
                {...register('password')}
              />
              {errors.password && (
                <span
                  id="reg-password-error"
                  role="alert"
                  aria-live="polite"
                  className="field-error"
                >
                  {errors.password.message}
                </span>
              )}
              {passwordValue && (
                <div
                  data-testid="password-strength"
                  aria-hidden="true"
                  style={{
                    height: 4,
                    borderRadius: 999,
                    background: 'var(--surface-3)',
                    overflow: 'hidden',
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${(strength.score + 1) * 25}%`,
                      background:
                        strength.score >= 3
                          ? 'var(--success)'
                          : strength.score === 2
                            ? 'var(--warning, orange)'
                            : 'var(--error, red)',
                      transition: 'width 150ms ease',
                    }}
                  />
                </div>
              )}
              {passwordValue && (
                <span className="muted" style={{ fontSize: 12 }}>
                  {t(`register.passwordStrength.${strength.label}`)}
                </span>
              )}
            </div>

            <label
              className="row"
              htmlFor="reg-agree-tos"
              style={{ gap: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', alignItems: 'flex-start' }}
            >
              <input
                id="reg-agree-tos"
                type="checkbox"
                style={{ accentColor: 'var(--primary)' }}
                aria-invalid={!!errors.agreeToTerms}
                aria-describedby={errors.agreeToTerms ? 'reg-tos-error' : undefined}
                {...register('agreeToTerms')}
              />
              <span>
                {t('register.tosLabelPrefix')}
                {/* Issue #109: Datenschutzerklärung wurde hier nur genannt, ohne
                    zu verlinken — Pflichtangabe muss verlinkt erreichbar sein. */}
                <a href="/privacy" target="_blank" rel="noreferrer">
                  {t('register.tosLabelPrivacyLink')}
                </a>
                {t('register.tosLabelSuffix')}
              </span>
            </label>
            {errors.agreeToTerms && (
              <span
                id="reg-tos-error"
                role="alert"
                aria-live="polite"
                className="field-error"
              >
                {errors.agreeToTerms.message}
              </span>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isSubmitting}
              aria-label={t('register.submitAriaLabel')}
              style={{ width: '100%' }}
              data-testid="submit-button"
            >
              {isSubmitting ? t('register.submitting') : t('register.submitLabel')}
            </button>
          </form>

          <div className="muted" style={{ fontSize: 13, textAlign: 'center' }}>
            {t('register.alreadyAccount')}{' '}
            <a href="/login" style={{ color: 'var(--primary)' }}>
              {t('register.loginLink')}
            </a>
          </div>
        </div>
      </div>

      {/* Rechte Spalte: Branding-Aside */}
      <aside className="auth-aside">
        <div className="row" style={{ gap: 10 }}>
          <div className="logo-mark">H</div>
          <div style={{ fontWeight: 600 }}>HeliumTracker</div>
        </div>

        <div>
          <h2 style={{ fontSize: 30, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {t('register.aside.headline')}
          </h2>
          <ol style={{ marginTop: 18, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <li className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', color: 'var(--primary-fg)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, flex: 'none' }}>1</div>
              <div>
                <strong>{t('register.aside.step1Title')}</strong>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t('register.aside.step1Desc')}</div>
              </div>
            </li>
            <li className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-3)', color: 'var(--text)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, flex: 'none' }}>2</div>
              <div>
                <strong>{t('register.aside.step2Title')}</strong>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t('register.aside.step2Desc')}</div>
              </div>
            </li>
            <li className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-3)', color: 'var(--text)', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, flex: 'none' }}>3</div>
              <div>
                <strong>{t('register.aside.step3Title')}</strong>
                <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{t('register.aside.step3Desc')}</div>
              </div>
            </li>
          </ol>
        </div>

        <div className="muted" style={{ fontSize: 11 }}>
          {t('aside.copyright')}
        </div>
      </aside>
    </div>
  )
}
